import type {
  Bundle,
  CarePlan,
  Goal,
  ServiceRequest,
} from '@medplum/fhirtypes';

import {
  type PlanInstanceSpec,
  compileInstanceSpec,
  instanceReplaceBundle,
} from '@eluve/plan-shapes';

// Accept = the in-place flip (ENG-4699): the draft CarePlan keeps its id but
// goes draft→active / proposal→plan, and its children are reconciled from the
// final tailored spec (delete the proposed ones, mint the accepted ones with
// phase choreography). One transaction, executed browser-side under the
// clinician's token. Identifier, encounter, and the match extension are
// preserved so the tab/callout still resolve the accepted plan.
export function buildAcceptBundle(params: {
  draftCarePlan: CarePlan;
  existingGoals: Goal[];
  existingServiceRequests: ServiceRequest[];
  finalSpec: PlanInstanceSpec;
  authorRef?: string;
  authorDisplay?: string;
}): Bundle {
  const {
    draftCarePlan,
    existingGoals,
    existingServiceRequests,
    finalSpec,
    authorRef,
    authorDisplay,
  } = params;

  const carePlanId = draftCarePlan.id;
  const patientRef = draftCarePlan.subject?.reference;
  if (!carePlanId || !patientRef) {
    throw new Error('Draft care plan is missing an id or patient');
  }

  const usesDraftAuthor = authorRef === undefined;
  const resolvedAuthorRef = authorRef ?? draftCarePlan.author?.reference;
  const resolvedAuthorDisplay =
    authorDisplay ??
    (usesDraftAuthor ? draftCarePlan.author?.display : undefined);

  const compiled = compileInstanceSpec(finalSpec, {
    patientRef,
    authorRef: resolvedAuthorRef,
    authorDisplay: resolvedAuthorDisplay,
    posture: 'accepted',
  });

  return instanceReplaceBundle({
    existing: draftCarePlan,
    existingGoals,
    existingServiceRequests,
    compiled,
    extension: draftCarePlan.extension,
  });
}

// executeBatch can resolve with per-entry failures in the response bundle rather
// than rejecting. Mirror the server-side save check (createdCarePlanId) so the
// browser Accept path doesn't report success over a partially-applied write.
export function assertBatchSucceeded(result: Bundle): void {
  const failures = (result.entry ?? [])
    .map((entry) => entry.response?.status)
    .filter((status): status is string => {
      return Number.parseInt(status ?? '0', 10) >= 400;
    });
  if (failures.length > 0) {
    throw new Error(
      `Care plan save failed: ${failures.length} resource(s) rejected (${failures.join(', ')})`,
    );
  }
}
